import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Coins, Medal, Path, Timer } from "@phosphor-icons/react";
import api, { apiErr } from "../api";
import { toast } from "sonner";
import ThreatDebrief from "../components/ThreatDebrief";

export default function HistoryDetail() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/history/${sessionId}`).then((r) => setD(r.data)).catch((e) => { toast.error(apiErr(e)); nav("/history"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (!d) return <div className="py-20 text-center text-ff-muted">Retrieving the record...</div>;

  const r = d.result || {};
  const positive = (r.gold_delta || 0) >= 0;

  const chosenLabel = (dec) => {
    const answered = d.decisions.find((x) => x.index === dec.index);
    return answered ? dec.options.find((o) => o.id === answered.option_id)?.label : "—";
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="history-detail">
      <button onClick={() => nav("/history")} className="mb-4 flex items-center gap-1 text-sm text-ff-muted hover:text-ff-gold" data-testid="history-back-btn">
        <ArrowLeft size={16} /> Back to history
      </button>

      <div className="ff-panel rounded-md p-6">
        <p className="font-mono-ff text-[10px] uppercase tracking-widest text-ff-brass">{d.region}</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">{d.expedition_name}</h1>
        <p className="text-xs text-ff-muted">{new Date(d.date).toLocaleString()}</p>

        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Outcome" value={r.outcome_label} />
          <Stat label="Stake" value={`${d.stake}`} />
          <Stat icon={Coins} label="Gold" value={`${positive ? "+" : ""}${r.gold_delta}`} color={positive ? "text-ff-success" : "text-ff-danger"} />
          <Stat icon={Medal} label="Reputation" value={`${r.reputation_delta >= 0 ? "+" : ""}${r.reputation_delta}`} />
        </div>

        <div className="mt-4 flex items-center gap-3 rounded-sm bg-ff-bg/50 p-3">
          <div className="flex -space-x-2">
            {d.crew.map((c) => <img key={c.id} src={c.portrait} alt={c.name} title={c.name} className="h-9 w-9 rounded-full border-2 border-ff-bg2 object-cover" />)}
          </div>
          <span className="text-xs text-ff-muted">Crew · Final Score <span className="font-mono-ff text-ff-gold">{r.final_score}</span></span>
        </div>

        {d.challenge?.submitted && (
          <div className="mt-3 flex items-center gap-2 text-xs text-ff-muted">
            <Timer size={14} className="text-ff-gold" /> Skill challenge: {d.challenge.band} ({d.challenge.score}/100)
          </div>
        )}
      </div>

      <div className="ff-panel mt-4 rounded-md p-6">
        <div className="mb-4 flex items-center gap-2">
          <Path size={20} weight="duotone" className="text-ff-gold" />
          <h2 className="font-display text-xl font-semibold">Decisions Made</h2>
        </div>
        <ol className="space-y-3">
          {d.decisions_content.map((dec) => (
            <li key={dec.index} className="rounded-sm border border-ff-brass/20 bg-ff-bg/40 p-3" data-testid={`detail-decision-${dec.index}`}>
              <p className="text-xs text-ff-muted">{dec.situation}</p>
              <p className="mt-1 text-sm font-medium text-ff-gold">→ {chosenLabel(dec)}</p>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-4">
        <ThreatDebrief debrief={d.debrief} />
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color = "text-ff-parchment" }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-ff-muted">{Icon && <Icon size={12} />}{label}</div>
      <div className={`font-mono-ff text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}
